import { useEffect, useMemo, useState } from "react";
import Pagination from "../components/Pagination";
import DashboardLayout from "../layout/DashboardLayout";
import {
  Activity,
  Archive,
  Eye,
  Pencil,
  Trash2,
  Plus,
  Search,
  Users,
  UserPlus,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import API from "../services/api";
import { canAddModule, canEditModule, getStoredUser, isAdminUser } from "../utils/auth";
import {
  cleanEmail,
  cleanPhone,
  firstValidationError,
  validateEmailField,
  validatePhoneField,
} from "../utils/validation";

const getOngoingTreatmentPlan = (patient) =>
  (patient.treatmentPlans || []).find((plan) => (plan.status || "active") === "active") || null;

const formatTreatmentPeriod = (plan) => {
  if (!plan?.fromDate && !plan?.toDate) {
    return "Dates not added";
  }

  return `${plan.fromDate || "Start not added"} to ${plan.toDate || "End not added"}`;
};

const getTreatmentTypeText = (plan) =>
  Array.isArray(plan?.treatmentTypes) && plan.treatmentTypes.length
    ? plan.treatmentTypes.join(", ")
    : "Treatment session";

const getDoneSessionCount = (plan) =>
  (plan?.sessionDays || []).filter((day) => day?.date && (day.status || "done") === "done").length;

export default function Patients() {
  const PAGE_SIZE = 10;
  const navigate = useNavigate();
  const currentUser = getStoredUser();
  const adminUser = isAdminUser(currentUser);
  const canAddPatients = canAddModule("patients", currentUser);
  const canEditPatients = canEditModule("patients", currentUser);
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [modalError, setModalError] = useState("");
  const [form, setForm] = useState({
    id: null,
    name: "",
    email: "",
    mobile: "",
    address: "",
  });
  const [showModal, setShowModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  const loadPatients = async () => {
    try {
      const response = await API.get("/patients");
      setPatients(response.data);
    } catch (loadError) {
      setError(loadError.response?.data?.message || "Failed to load patients.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPatients();
  }, []);

  const resetForm = () => {
    setForm({ id: null, name: "", email: "", mobile: "", address: "" });
  };

  const closeModal = () => {
    setShowModal(false);
    resetForm();
    setError("");
    setModalError("");
  };

  const openAddModal = () => {
    resetForm();
    setError("");
    setModalError("");
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setModalError("");

    const validationError = firstValidationError([
      !form.name.trim() ? "Patient name is required." : "",
      validateEmailField(form.email, false),
      validatePhoneField(form.mobile),
    ]);

    if (validationError) {
      setModalError(validationError);
      setSaving(false);
      return;
    }

    const normalizedEmail = cleanEmail(form.email);
    const normalizedMobile = cleanPhone(form.mobile);
    const duplicatePatient = patients.find(
      (patient) =>
        patient.id !== form.id &&
        ((normalizedEmail && cleanEmail(patient.email) === normalizedEmail) ||
          cleanPhone(patient.mobile) === normalizedMobile)
    );

    if (duplicatePatient) {
      setModalError(
        cleanPhone(duplicatePatient.mobile) === normalizedMobile
          ? "A patient with this mobile number already exists."
          : "A patient with this email address already exists."
      );
      setSaving(false);
      return;
    }

    try {
      const patientPayload = {
        name: form.name.trim(),
        mobile: normalizedMobile,
        address: form.address.trim(),
      };
      if (normalizedEmail) {
        patientPayload.email = normalizedEmail;
      }

      if (form.id) {
        await API.put(`/patients/${form.id}`, {
          name: form.name.trim(),
          email: normalizedEmail,
          mobile: normalizedMobile,
          address: form.address.trim(),
        });
      } else {
        await API.post("/patients", patientPayload);
      }

      await loadPatients();
      closeModal();
    } catch (saveError) {
      setModalError(saveError.response?.data?.message || "Failed to save patient.");
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (patient) => {
    setForm(patient);
    setError("");
    setModalError("");
    setShowModal(true);
  };

  const confirmDelete = async () => {
    try {
      await API.delete(`/patients/${deleteId}`);
      setPatients((current) => current.filter((patient) => patient.id !== deleteId));
      setDeleteId(null);
      setError("");
    } catch (deleteError) {
      setError(deleteError.response?.data?.message || "Failed to archive patient.");
    }
  };

  const filteredPatients = useMemo(() => {
    const keyword = search.toLowerCase();
    return patients.filter((patient) => {
      return (
        patient.name.toLowerCase().includes(keyword) ||
        patient.email.toLowerCase().includes(keyword) ||
        patient.mobile.includes(keyword) ||
        (patient.address || "").toLowerCase().includes(keyword)
      );
    });
  }, [patients, search]);

  const ongoingTreatmentPatients = useMemo(
    () =>
      filteredPatients
        .map((patient) => ({
          ...patient,
          ongoingPlan: getOngoingTreatmentPlan(patient),
        }))
        .filter((patient) => patient.ongoingPlan)
        .sort((a, b) => String(a.ongoingPlan.toDate || "").localeCompare(String(b.ongoingPlan.toDate || ""))),
    [filteredPatients]
  );

  const ongoingTreatmentCount = useMemo(
    () => patients.filter((patient) => getOngoingTreatmentPlan(patient)).length,
    [patients]
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [search]);

  const totalPages = Math.max(1, Math.ceil(filteredPatients.length / PAGE_SIZE));
  const paginatedPatients = useMemo(() => {
    const startIndex = (currentPage - 1) * PAGE_SIZE;
    return filteredPatients.slice(startIndex, startIndex + PAGE_SIZE);
  }, [currentPage, filteredPatients]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const statCards = [
    {
      label: "Total Patients",
      value: patients.length,
      icon: Users,
      tone: "bg-blue-50 text-blue-600",
    },
    {
      label: "Visible Results",
      value: filteredPatients.length,
      icon: UserPlus,
      tone: "bg-emerald-50 text-emerald-600",
    },
    {
      label: "Treatment Ongoing",
      value: ongoingTreatmentCount,
      icon: Activity,
      tone: "bg-cyan-50 text-cyan-700",
    },
  ];

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="motion-panel rounded-2xl border border-slate-200 bg-gradient-to-r from-blue-950 via-slate-900 to-cyan-900 px-5 py-4 text-white shadow-md">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div className="space-y-2">
              <p className="text-sm uppercase tracking-[0.2em] text-white/60">
                Patient Directory
              </p>
              <div>
                <h1 className="text-2xl font-semibold">Patients</h1>
                <p className="mt-1 max-w-2xl text-sm text-white/75">
                  Manage patient records, review contact details, and quickly open
                  individual profiles from one clean dashboard.
                </p>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              {adminUser ? (
                <button
                  onClick={() => navigate("/patients/archive")}
                  className="inline-flex items-center justify-center gap-2 rounded-xl border border-white/20 bg-white/10 px-4 py-2 text-sm font-medium text-white hover:bg-white/15"
                >
                  <Archive size={16} /> Archive
                </button>
              ) : null}

              {canAddPatients ? (
                <button
                  onClick={openAddModal}
                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-white px-4 py-2 text-sm font-medium text-slate-900 hover:bg-slate-100"
                >
                  <Plus size={16} /> Add Patient
                </button>
              ) : null}
            </div>
          </div>
        </div>

        {error && (
          <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
            {error}
          </div>
        )}

        <div className="stagger-grid grid gap-4 md:grid-cols-3">
          {statCards.map(({ label, value, icon: Icon, tone }) => (
            <div
              key={label}
              className="motion-card rounded-3xl border border-slate-200 bg-white p-5 shadow-sm"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">{label}</p>
                  <p className="mt-2 text-3xl font-semibold text-slate-900">
                    {loading ? "..." : value}
                  </p>
                </div>
                <div className={`rounded-2xl p-3 ${tone}`}>
                  <Icon size={22} />
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="motion-card rounded-3xl border border-cyan-100 bg-[linear-gradient(135deg,#f8ffff,#ffffff_45%,#f0fdfa)] p-6 shadow-sm">
          <div className="mb-5 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <p className="text-sm font-semibold uppercase tracking-[0.18em] text-cyan-700">
                Treatment Ongoing
              </p>
              <h2 className="mt-1 text-xl font-semibold text-slate-900">
                Patients With Running Sessions
              </h2>
              <p className="mt-1 text-sm text-slate-500">
                Active treatment sessions from patient profile, shown separately for quick follow-up.
              </p>
            </div>
            <span className="inline-flex w-fit items-center gap-2 rounded-2xl bg-cyan-50 px-4 py-2 text-sm font-semibold text-cyan-700">
              <Activity size={16} />
              {loading ? "..." : `${ongoingTreatmentPatients.length} active`}
            </span>
          </div>

          {!loading && ongoingTreatmentPatients.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-cyan-200 bg-white/70 px-4 py-8 text-center text-sm text-slate-500">
              No patients currently have an ongoing treatment session.
            </div>
          ) : (
            <div className="grid gap-3 lg:grid-cols-2">
              {ongoingTreatmentPatients.map((patient) => (
                <button
                  key={`ongoing-${patient.id}`}
                  type="button"
                  onClick={() => navigate(`/patients/${patient.id}`)}
                  className="group rounded-3xl border border-cyan-100 bg-white p-4 text-left shadow-sm transition hover:-translate-y-0.5 hover:border-cyan-200 hover:shadow-md"
                >
                  <div className="flex items-start gap-3">
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-cyan-50 text-lg font-semibold text-cyan-700">
                      {patient.name.charAt(0)}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="truncate text-base font-semibold text-slate-900">
                          {patient.name}
                        </p>
                        <span className="rounded-full bg-emerald-50 px-2.5 py-1 text-[11px] font-semibold text-emerald-700">
                          Active
                        </span>
                      </div>
                      <p className="mt-1 text-sm text-slate-500">{patient.mobile}</p>
                      <p className="mt-3 line-clamp-2 text-sm font-medium text-slate-800">
                        {getTreatmentTypeText(patient.ongoingPlan)}
                      </p>
                      <p className="mt-1 text-xs font-semibold text-emerald-700">
                        {getDoneSessionCount(patient.ongoingPlan)} days done
                      </p>
                      <p className="mt-1 text-xs text-slate-500">
                        {formatTreatmentPeriod(patient.ongoingPlan)}
                      </p>
                    </div>
                    <Eye
                      size={18}
                      className="mt-1 shrink-0 text-cyan-600 transition group-hover:scale-110"
                    />
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="motion-card rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h2 className="text-xl font-semibold text-slate-900">
                Patient List
              </h2>
              <p className="text-sm text-slate-500">
                Search, review, edit, or open a patient profile.
              </p>
            </div>

            <div className="relative w-full max-w-md">
              <Search
                size={18}
                className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
              />
              <input
                type="text"
                placeholder="Search by name, email, or mobile"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full rounded-2xl border border-slate-200 bg-slate-50 py-3 pl-11 pr-4 text-sm outline-none focus:border-blue-300 focus:bg-white focus:ring-4 focus:ring-blue-100"
              />
            </div>
          </div>

          <div className="overflow-hidden rounded-3xl border border-slate-200">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[760px] text-left">
                <thead className="bg-slate-50 text-sm text-slate-500">
                  <tr>
                    <th className="px-5 py-4 font-medium">No.</th>
                    <th className="px-5 py-4 font-medium">Patient</th>
                    <th className="px-5 py-4 font-medium">Email</th>
                    <th className="px-5 py-4 font-medium">Mobile</th>
                    <th className="px-5 py-4 font-medium text-center">Actions</th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-slate-100">
                  {!loading && filteredPatients.length === 0 ? (
                    <tr>
                      <td
                        colSpan="5"
                        className="px-5 py-14 text-center text-sm text-slate-500"
                      >
                        No patients found for your search.
                      </td>
                    </tr>
                  ) : (
                    paginatedPatients.map((patient, index) => (
                      <tr
                        key={patient.id}
                        className="bg-white transition hover:bg-slate-50"
                      >
                        <td className="px-5 py-4 font-medium text-slate-600">
                          {(currentPage - 1) * PAGE_SIZE + index + 1}
                        </td>
                        <td className="px-5 py-4">
                          <div className="flex items-center gap-3">
                            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-blue-50 font-semibold text-blue-600">
                              {patient.name.charAt(0)}
                            </div>
                            <div>
                              <p className="font-medium text-slate-900">
                                {patient.name}
                              </p>
                              <p className="text-sm text-slate-500">
                                ID {patient.patientId || "Not assigned"}
                              </p>
                              {patient.address ? (
                                <p className="mt-0.5 max-w-xs truncate text-xs text-slate-400">
                                  {patient.address}
                                </p>
                              ) : null}
                            </div>
                          </div>
                        </td>
                        <td className="px-5 py-4 text-slate-600">
                          {patient.email || "Not provided"}
                        </td>
                        <td className="px-5 py-4 text-slate-600">
                          {patient.mobile}
                        </td>
                        <td className="px-5 py-4">
                          <div className="flex items-center justify-center gap-2">
                            <button
                              onClick={() => navigate(`/patients/${patient.id}`)}
                              className="rounded-xl border border-blue-200 bg-blue-50 p-2 text-blue-600 hover:bg-blue-100"
                              title="View profile"
                            >
                              <Eye size={17} />
                            </button>

                            {canEditPatients ? (
                              <>
                                <button
                                  onClick={() => handleEdit(patient)}
                                  className="rounded-xl border border-emerald-200 bg-emerald-50 p-2 text-emerald-600 hover:bg-emerald-100"
                                  title="Edit patient"
                                >
                                  <Pencil size={17} />
                                </button>

                                <button
                                  onClick={() => setDeleteId(patient.id)}
                                  className="rounded-xl border border-rose-200 bg-rose-50 p-2 text-rose-600 hover:bg-rose-100"
                                  title="Archive patient"
                                >
                                  <Trash2 size={17} />
                                </button>
                              </>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={filteredPatients.length}
            pageSize={PAGE_SIZE}
            onPageChange={setCurrentPage}
            label="patients"
          />
        </div>

        {showModal && (canAddPatients || canEditPatients) ? (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-sm px-4">
            <div className="motion-card w-full max-w-lg rounded-[28px] border border-slate-200 bg-white p-6 shadow-2xl">
              <div className="mb-5 flex items-start justify-between">
                <div>
                  <p className="text-sm font-medium text-blue-600">
                    Patient Form
                  </p>
                  <h3 className="text-2xl font-semibold text-slate-900">
                    {form.id ? "Edit Patient" : "Add Patient"}
                  </h3>
                </div>
              </div>

              {modalError ? (
                <div className="mb-4 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                  {modalError}
                </div>
              ) : null}

              <form onSubmit={handleSubmit} className="space-y-4">
                <input
                  type="text"
                  placeholder="Name"
                  className="input"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                />

                <input
                  type="email"
                  placeholder="Email"
                  className="input"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                />

                <input
                  type="text"
                  placeholder="Mobile"
                  className="input"
                  value={form.mobile}
                  onChange={(e) => setForm({ ...form, mobile: e.target.value })}
                  required
                />

                <textarea
                  placeholder="Address"
                  className="input min-h-[96px] resize-none"
                  value={form.address}
                  onChange={(e) => setForm({ ...form, address: e.target.value })}
                />

                <div className="flex justify-end gap-3 pt-2">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="rounded-xl border border-slate-200 px-4 py-2 font-medium text-slate-600 hover:bg-slate-50"
                  >
                    Cancel
                  </button>

                  <button
                    type="submit"
                    disabled={saving}
                    className="rounded-xl bg-slate-900 px-5 py-2 font-medium text-white hover:bg-slate-800 disabled:opacity-60"
                  >
                    {saving ? "Saving..." : "Save"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        ) : null}

        {deleteId && canEditPatients ? (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-sm px-4">
            <div className="motion-card w-full max-w-md rounded-[28px] border border-slate-200 bg-white p-6 shadow-2xl">
              <p className="text-sm font-medium text-amber-600">Archive Patient</p>
              <h3 className="mt-1 text-2xl font-semibold text-slate-900">
                Move To Archive
              </h3>

              <p className="mt-3 text-sm leading-6 text-slate-500">
                Are you sure you want to archive this patient? The record will be
                hidden from the patient list and other modules until an admin
                permanently deletes it from the archive.
              </p>

              <div className="mt-6 flex justify-end gap-3">
                <button
                  onClick={() => setDeleteId(null)}
                  className="rounded-xl border border-slate-200 px-4 py-2 font-medium text-slate-600 hover:bg-slate-50"
                >
                  Cancel
                </button>

                <button
                  onClick={confirmDelete}
                  className="rounded-xl bg-amber-600 px-5 py-2 font-medium text-white hover:bg-amber-700"
                >
                  Archive
                </button>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </DashboardLayout>
  );
}
